/*
 * What a recruiter sees when a screen throws.
 *
 * Before this there was no error boundary anywhere in the app — no `errorComponent`, no
 * `componentDidCatch`. A single render throw unmounted the router, so the whole shell went with it:
 * nav, header, scope selector, everything. The user was left on a blank page with no route back and
 * no indication that reloading would help.
 *
 * Two layers, because they catch different things:
 *
 *   - `ErrorScreen` is wired as the router's `defaultErrorComponent` and on the root route. It
 *     catches anything thrown while rendering a screen, and it keeps the shell — the failure is
 *     scoped to the region that failed, which is the whole point of a boundary.
 *   - `AppErrorBoundary` wraps the `RouterProvider` itself. Nothing inside the router can reach it,
 *     which is exactly why it exists: a throw in the router's own render, or in a provider above the
 *     route tree, has no route-level boundary to be caught by.
 *
 * **`reset` is offered before `reload`.** A render throw is frequently transient — a query that
 * resolved to a shape a screen did not expect, refetched successfully a moment later. Re-rendering
 * costs the user nothing; a full reload costs them every unsaved thing on the screen.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Banner, Button, Card, FullBleed, Stack } from '@indovation/design-system';
import { useI18n } from '@indovation/i18n/react';

/** Narrowed here rather than at each call site: `unknown` is what both boundaries actually receive. */
function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export interface ErrorScreenProps {
  error: unknown;
  /** Re-renders the failed subtree. Absent when there is nothing to retry. */
  reset?: () => void;
}

/**
 * The route-level error screen. Keeps the shell — it renders inside `Outlet`.
 *
 * The message goes in a `details`, closed. A recruiter does not need it and it would be noise above
 * the fold; support does need it, and asking somebody to open a browser console is not a thing this
 * product is going to ask.
 */
export function ErrorScreen({ error, reset }: ErrorScreenProps) {
  const { t } = useI18n();

  return (
    <Card title={t('error.title')} description={t('error.description')}>
      <Stack gap={4}>
        <Banner tone="danger" dismiss={{ label: t('common.dismiss') }}>{t('error.reassurance')}</Banner>

        <details className="tf-error-details">
          <summary>{t('error.details')}</summary>
          <p className="tf-error-details__body">{messageOf(error)}</p>
        </details>

        <Stack direction="row" gap={2} wrap>
          {reset ? <Button onClick={reset}>{t('error.retry')}</Button> : null}
          <Button variant="ghost" onClick={() => window.location.reload()}>
            {t('error.reload')}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

interface BoundaryState {
  error: unknown;
}

/**
 * One region of a screen, failing without taking the screen with it — added 13 Aug 2026.
 *
 * The job record is seventeen panels in one scrolling document, and until this existed a throw in
 * any one of them replaced all seventeen: the intake verdict, the pipeline, the activity and the
 * requirement itself all disappeared because, say, the workbench got a shape it did not expect.
 * That is a bad trade at one panel and an absurd one at seventeen, and §12.1 of the job module
 * requirements says the workbench in particular "never blocks the page".
 *
 * So the failure is scoped to the thing that failed, and the message names it — "the workbench
 * could not be shown" is actionable in a way that "something went wrong" is not, because the
 * reader can see the other sixteen sections working and knows what to report.
 *
 * `reset` is offered here too, for the reason the route-level screen gives: a render throw is
 * often a query that resolved to an unexpected shape and refetched correctly a moment later.
 */
export class SectionErrorBoundary extends Component<
  { children: ReactNode; label: string },
  BoundaryState
> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  render() {
    if (this.state.error === null) return this.props.children;

    return (
      <SectionFailure
        label={this.props.label}
        error={this.state.error}
        reset={() => this.setState({ error: null })}
      />
    );
  }
}

/** The fallback, as a function component so it can reach the catalogue. */
function SectionFailure({
  label,
  error,
  reset,
}: {
  label: string;
  error: unknown;
  reset: () => void;
}) {
  const { t } = useI18n();

  return (
    <Card title={t('error.section.title', { section: label })}>
      <Stack gap={3}>
        <Banner tone="danger" dismiss={{ label: t('common.dismiss') }}>{t('error.section.description')}</Banner>

        <details className="tf-error-details">
          <summary>{t('error.details')}</summary>
          <p className="tf-error-details__body">{messageOf(error)}</p>
        </details>

        <Stack direction="row" gap={2} wrap>
          <Button variant="ghost" onClick={reset}>
            {t('error.retry')}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * The outermost boundary. A class, because React still offers no hook equivalent — `componentDidCatch`
 * and `getDerivedStateFromError` have no function-component form.
 *
 * This one renders `FullBleed` rather than a card inside the shell, and the reason is honest: if the
 * throw came from above the route tree, the shell is precisely what could not be rendered. Pretending
 * otherwise would mean this boundary throwing while rendering its own fallback.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // Deliberately kept: this is the only record that the outermost boundary fired, and it fires for
    // failures no route-level handler saw.
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    if (this.state.error === null) return this.props.children;

    return (
      <FullBleed panelExempt="The shell itself failed to render; there is no region to reserve.">
        <ErrorScreen
          error={this.state.error}
          reset={() => this.setState({ error: null })}
        />
      </FullBleed>
    );
  }
}
